import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Zap, X } from "lucide-react";

// "chauffeur" (Essentiel) retiré de la vente (refonte d'offre Solo/Pro/Flotte,
// 2026-08-30) — plus aucune route backend ne renvoie required_plan="chauffeur"
// (voir app/core/plan_guard.py côté API), donc plus d'entrée ici non plus :
// un plan qu'on ne peut plus acheter ne doit jamais apparaître dans ce modal.
const PLAN_COLORS  = { pro: "#158d38", flotte: "#7c3aed", founder: "#d97706" };
const PLAN_LABELS  = { pro: "Pro", flotte: "Flotte", founder: "Fondateur" };
// Flotte n'a plus de prix fixe (sur devis, 2026-08-30) — pas d'entrée ici,
// le message retombe alors sur `message || "Passez au plan Flotte..."`.
const PLAN_PRICES  = { pro: "19,90 €/mois" };
const PLAN_PERKS   = {
  pro:    ["Suivi carburant et électrique", "Estimation URSSAF", "Récapitulatif TVA", "Devis", "Export FEC", "Module comptabilité", "Support prioritaire"],
  flotte: ["Multi-véhicules", "Gestion d'équipe", "Support dédié"],
};

/**
 * UpgradeModal — modal global déclenché par l'intercepteur axios sur 403 UPGRADE_REQUIRED.
 * Monté une seule fois dans App.js au-dessus du BrowserRouter.
 * Écoute l'événement window « upgrade_required » dispatché par api.js.
 */
export default function UpgradeModal() {
  const navigate = useNavigate();
  const [state, setState] = useState(null); // null = fermé | { required_plan, message }

  const handleEvent = useCallback((e) => {
    setState(e.detail);
  }, []);

  useEffect(() => {
    window.addEventListener("upgrade_required", handleEvent);
    return () => window.removeEventListener("upgrade_required", handleEvent);
  }, [handleEvent]);

  const close = useCallback(() => setState(null), []);

  const goPlans = useCallback(() => {
    setState(null);
    navigate("/plans");
  }, [navigate]);

  if (!state) return null;

  const plan    = state.required_plan || "pro";
  const color   = PLAN_COLORS[plan]  || "#158d38";
  const label   = PLAN_LABELS[plan]  || plan.charAt(0).toUpperCase() + plan.slice(1);
  const price   = PLAN_PRICES[plan]  || "";
  const perks   = PLAN_PERKS[plan]   || [];
  const message = state.message;

  return (
    <div style={s.backdrop} onClick={close}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button style={s.closeBtn} onClick={close} aria-label="Fermer">
          <X size={18} />
        </button>

        {/* Icon */}
        <div style={{ ...s.iconCircle, background: color + "18", color }}>
          <Lock size={28} />
        </div>

        {/* Title */}
        <h2 style={s.title}>
          Fonctionnalité réservée au plan{" "}
          <span style={{ color }}>{label}</span>
        </h2>

        {/* Subtitle */}
        <p style={s.sub}>
          {price
            ? `Débloquez cette fonctionnalité à partir de ${price}.`
            : message || `Passez au plan ${label} pour y accéder.`}
        </p>

        {/* Perks */}
        {perks.length > 0 && (
          <div style={s.perkList}>
            {perks.map((f, i) => (
              <div key={i} style={s.perkRow}>
                <span style={{ ...s.check, color }}>&#10003;</span>
                <span style={s.perkText}>{f}</span>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <button onClick={goPlans} style={{ ...s.btn, background: color }}>
          <Zap size={15} style={{ verticalAlign: "middle", marginRight: 6 }} />
          Voir les plans →
        </button>

        <button onClick={close} style={s.skipBtn}>Rester sur ce plan</button>
      </div>
    </div>
  );
}

const s = {
  backdrop: {
    position: "fixed", inset: 0, zIndex: 9999,
    background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "16px",
  },
  modal: {
    background: "#fff", borderRadius: "20px", padding: "40px 36px",
    maxWidth: "420px", width: "100%",
    display: "flex", flexDirection: "column", alignItems: "center", gap: "16px",
    textAlign: "center", fontFamily: "'DM Sans', sans-serif",
    boxShadow: "0 24px 80px rgba(0,0,0,0.2)",
    position: "relative",
  },
  closeBtn: {
    position: "absolute", top: "16px", right: "16px",
    background: "none", border: "none", cursor: "pointer",
    color: "#9aaa9a", display: "flex", alignItems: "center", padding: "4px",
  },
  iconCircle: {
    width: "64px", height: "64px", borderRadius: "20px",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  title: { fontSize: "18px", fontWeight: "800", color: "#181d27", lineHeight: 1.4, margin: 0 },
  sub: { fontSize: "14px", color: "#6b7b6b", lineHeight: 1.6, margin: 0 },
  perkList: {
    width: "100%", display: "flex", flexDirection: "column", gap: "8px",
    background: "#f8faf8", borderRadius: "12px", padding: "16px 20px", textAlign: "left",
  },
  perkRow: { display: "flex", gap: "10px", alignItems: "flex-start" },
  check: { fontSize: "13px", fontWeight: "800", flexShrink: 0, marginTop: "1px" },
  perkText: { fontSize: "13px", color: "#374737", lineHeight: 1.4 },
  btn: {
    width: "100%", padding: "14px", borderRadius: "12px", border: "none",
    color: "white", fontSize: "15px", fontWeight: "700", cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
  },
  skipBtn: {
    background: "none", border: "none", color: "#9aaa9a",
    fontSize: "13px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
  },
};
